'use client';

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';

export interface Contractor {
  id: number;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  is_active: boolean;
  client_id: number;
}

interface UseContractorsReturn {
  contractors: Contractor[];
  loading: boolean;
  error: string | null;
  getContractorName: (contactId: number) => string;
  refreshContractors: () => Promise<void>;
}

export function useContractors(includeInactive = false): UseContractorsReturn {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContractors = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const url = `/api/v1/contacts/contractors${includeInactive ? '?include_inactive=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load contractors: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setContractors(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to load contractors');
      }
    } catch (err) {
      console.error('Error loading contractors:', err);
      setError(err instanceof Error ? err.message : 'Failed to load contractors');
      setContractors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContractors();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const getContractorName = useCallback((contactId: number): string => {
    const contractor = contractors.find(c => c.id === contactId);
    if (!contractor) return 'Unknown Contractor';
    return contractor.company_name || contractor.contact_person || `Contractor #${contactId}`;
  }, [contractors]);

  return {
    contractors,
    loading,
    error,
    getContractorName,
    refreshContractors: loadContractors
  };
}
